/**
 * Sending files in Presence chat, the way WhatsApp does it.
 *
 * The message is in the thread the moment Send is pressed — the photo, video,
 * voice note or document card painted from the file on this device — with a
 * ring that fills as it uploads and an ✗ to stop it. The composer clears at
 * once, so the user can keep typing. A message that is stopped or fails keeps
 * its place with Retry and Delete. Before this, Send uploaded everything first
 * with nothing on screen, then sent: the tray just sat there until the bytes
 * had gone, and a second tap on Send was silently ignored.
 *
 * The optimistic row is an ordinary `pendingSends` entry, exactly as a text
 * send makes one, carrying local object URLs until the upload is done; this
 * module tracks how far along it is (store/upload-outbox.store.ts) and swaps
 * the stored URLs in on reconcile. It is module state rather than a hook so
 * the upload carries on when the user opens another conversation: the panel
 * and its composer unmount, the upload does not.
 *
 * Broadcast lists do not come through here: their send is one REST call that
 * carries a body only (useSendMessage), so they keep the old path.
 */

import { useAuthStore } from '@/features/auth/store/auth.store'
import { rememberLocalCopy } from '@/lib/local-media-copies'
import { isAbortError, type UploadProgress } from '@/lib/send-with-progress'
import { checkFileForUpload } from '@/lib/upload-policy'
import { useMessengerStore } from '../store/messenger.store'
import { useUploadOutbox } from '../store/upload-outbox.store'
import { kindForMime, probeMedia, type ProbedMedia } from './media-probe'
import { messengerApi, MessengerApiError } from './messenger.api'
import { sendMessageToConversation } from './messenger.socket'
import type { Attachment, AttachmentKind, Message as WireMessage } from './messenger.wire'

export interface OutgoingFile {
  file: File
  /** Overrides the mime-derived kind — needed for `voice`, which mime cannot tell from audio. */
  kind?: AttachmentKind
  /** Known up front for a recorded voice note; probed otherwise. */
  durationMs?: number
}

export interface OutgoingMessage {
  conversationId: string
  body?: string
  replyTo?: string | null
  files: OutgoingFile[]
}

/** A probe that never answers (a codec the browser cannot seek) must not hold the send. */
const PROBE_TIMEOUT_MS = 4000

function probeWithin(file: File, kind: AttachmentKind): Promise<ProbedMedia> {
  const empty: ProbedMedia = { kind, width: null, height: null, durationMs: null, poster: null }
  return Promise.race([
    probeMedia(file, kind),
    new Promise<ProbedMedia>((resolve) => setTimeout(() => resolve(empty), PROBE_TIMEOUT_MS)),
  ])
}

/** Upload bytes and resolve them to a URL the SDK can persist. */
async function putAndResolve(
  file: File,
  opts: { onProgress?: UploadProgress; signal?: AbortSignal } = {},
): Promise<{ id: string; url: string }> {
  // Private: a chat file is for the conversation it is sent in (and any it is
  // forwarded to), opened through a link signed for its members.
  const { attachment } = await messengerApi.uploadAttachment(file, { private: true, ...opts })
  // The upload response may already carry a URL (S3 public base); otherwise ask
  // the discovery endpoint, which returns the durable `/raw` path for the local
  // backend. Either way it must end up absolute — the SDK rejects relative URLs.
  const raw = attachment.url ?? (await messengerApi.getAttachmentUrl(attachment.id)).url
  return { id: attachment.id, url: messengerApi.absoluteAttachmentUrl(raw) }
}

/**
 * Check, probe and upload one file (and its poster), and describe it the way
 * the SDK stores it. Shared with `useAttachmentUpload`, which broadcasts use.
 *
 * `probed` skips the probe when the caller already has it.
 */
export async function uploadAttachmentFile(
  file: File,
  kind: AttachmentKind,
  opts: { onProgress?: UploadProgress; signal?: AbortSignal; probed?: ProbedMedia; durationMs?: number } = {},
): Promise<{ attachment: Attachment; probed: ProbedMedia }> {
  // Refuse before probing or uploading. The pickers already filter, but
  // recorded, captured and dropped files reach this too.
  const check = await checkFileForUpload(file, 'attachment')
  if (!check.ok) throw new MessengerApiError(check.message, check.code, 0)
  const probed = opts.probed ?? (await probeWithin(file, kind))
  const { id, url } = await putAndResolve(file, { onProgress: opts.onProgress, signal: opts.signal })

  // A video poster is a second object in the same storage. Losing it costs a
  // thumbnail, never the attachment — so it never throws. Not cancellable: it
  // is a few kilobytes, and stopped between the file and its poster, the file
  // would be kept for Retry without one, and arrive with no preview.
  let thumbnailUrl: string | null = null
  if (probed.poster) {
    thumbnailUrl = await putAndResolve(probed.poster)
      .then((r) => r.url)
      .catch(() => null)
  }

  return {
    probed,
    attachment: {
      id,
      url,
      kind,
      mime: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      filename: file.name || null,
      thumbnailUrl,
      width: probed.width,
      height: probed.height,
      durationMs: opts.durationMs ?? probed.durationMs,
    },
  }
}

/* ===== The outbox ===== */

interface Job {
  message: OutgoingMessage
  /** Per file: what the bubble paints until the stored copy replaces it. */
  local: Array<{ url: string; posterUrl: string | null }>
  /** Per file, once stored — kept so Retry uploads only what did not make it. */
  uploaded: Array<Attachment | null>
  probed: Array<ProbedMedia | null>
  /** Bytes sent per file, for the one ring the message shows. */
  sent: number[]
  controller: AbortController | null
}

const jobs = new Map<string, Job>()

function randomUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'temp-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function kindOf(f: OutgoingFile): AttachmentKind {
  return f.kind ?? kindForMime(f.file.type)
}

/** The row in the thread: the real message's shape, painted from local files. */
function draftAttachments(job: Job): Attachment[] {
  return job.message.files.map((f, i) => {
    const done = job.uploaded[i]
    if (done) return done
    const probed = job.probed[i]
    return {
      id: `local-${i}`,
      url: job.local[i].url,
      kind: kindOf(f),
      mime: f.file.type || 'application/octet-stream',
      sizeBytes: f.file.size,
      filename: f.file.name || null,
      thumbnailUrl: job.local[i].posterUrl,
      width: probed?.width ?? null,
      height: probed?.height ?? null,
      durationMs: f.durationMs ?? probed?.durationMs ?? null,
    }
  })
}

/** Still in the thread? Clearing the chat or deleting the conversation drops it. */
function stillPending(messageId: string): boolean {
  return useMessengerStore.getState().pendingSends.has(messageId)
}

function releaseLocal(job: Job): void {
  for (const l of job.local) {
    URL.revokeObjectURL(l.url)
    if (l.posterUrl) URL.revokeObjectURL(l.posterUrl)
  }
}

/** Words for the user, from whatever the upload or the send threw. */
function failureMessage(err: unknown, stage: 'upload' | 'send'): string {
  if (stage === 'send') return 'The message could not be sent. Check your connection and try again.'
  if (err instanceof MessengerApiError) {
    // Policy refusals (status 0) and the server's own reasons are already
    // sentences; a bare status is not.
    if (err.status === 413) return 'The server would not accept a file this large.'
    if (!/^attachment upload \d+$/.test(err.message)) return err.message
  }
  if (err instanceof TypeError) return 'The file could not be uploaded. Check your connection and try again.'
  return 'The file could not be uploaded. Please try again.'
}

/**
 * Put a file message in the thread and start sending it. Returns at once with
 * the message's id; the bubble shows the rest. Null when there is nobody
 * signed in to send as.
 */
export function sendFilesMessage(message: OutgoingMessage): string | null {
  const me = useAuthStore.getState().user
  const presenceId = useAuthStore.getState().presenceId
  if (!me?.id || !presenceId || message.files.length === 0) return null

  const messageId = randomUuid()
  const job: Job = {
    message,
    local: message.files.map((f) => ({ url: URL.createObjectURL(f.file), posterUrl: null })),
    uploaded: message.files.map(() => null),
    probed: message.files.map(() => null),
    sent: message.files.map(() => 0),
    controller: null,
  }
  jobs.set(messageId, job)

  const temp: WireMessage = {
    id: messageId,
    conversationId: message.conversationId,
    senderId: presenceId,
    type: 'text',
    body: message.body || null,
    replyTo: message.replyTo ?? null,
    callPayload: null,
    editedAt: null,
    deletedAt: null,
    createdAt: new Date().toISOString(),
    reactions: [],
    attachments: draftAttachments(job),
  }
  useMessengerStore.getState().addPendingSend(temp)
  useUploadOutbox.getState().put({
    messageId,
    conversationId: message.conversationId,
    progress: 0,
    state: 'uploading',
  })
  void run(messageId)
  return messageId
}

/**
 * The ✗ on the ring: stop uploading, keep the message. It stays in the thread
 * with Retry and Delete; files that had already finished are kept, so Retry
 * sends only the rest.
 */
export function stopFilesMessage(messageId: string): void {
  const job = jobs.get(messageId)
  if (!job) return
  // Marked before the abort, so the run that is cut short finds it stopped
  // rather than reporting the abort as a failure.
  useUploadOutbox.getState().patch(messageId, { state: 'stopped', error: undefined })
  job.controller?.abort()
  job.controller = null
}

/** Delete, on a stopped or failed message: it goes entirely. */
export function cancelFilesMessage(messageId: string): void {
  const job = jobs.get(messageId)
  job?.controller?.abort()
  jobs.delete(messageId)
  useMessengerStore.getState().rollbackPendingSend(messageId)
  useUploadOutbox.getState().drop(messageId)
  if (job) releaseLocal(job)
}

export function retryFilesMessage(messageId: string): void {
  if (jobs.has(messageId)) void run(messageId)
}

async function run(messageId: string): Promise<void> {
  const job = jobs.get(messageId)
  if (!job) return
  job.controller?.abort()
  const controller = new AbortController()
  job.controller = controller
  const outbox = useUploadOutbox.getState

  const total = job.message.files.reduce((sum, f) => sum + f.file.size, 0) || 1
  // Whole percents only: XHR reports every few kilobytes, and each report is a
  // store write and a render of the bubble.
  let lastPct = -1
  const report = () => {
    const pct = Math.floor((job.sent.reduce((a, b) => a + b, 0) / total) * 100)
    if (pct === lastPct) return
    lastPct = pct
    outbox().patch(messageId, { progress: pct / 100 })
  }
  job.message.files.forEach((f, i) => {
    job.sent[i] = job.uploaded[i] ? f.file.size : 0
  })
  outbox().patch(messageId, { state: 'uploading', error: undefined })
  report()

  let stage: 'upload' | 'send' = 'upload'
  try {
    await Promise.all(
      job.message.files.map(async (f, i) => {
        if (job.uploaded[i]) return
        const kind = kindOf(f)
        // Probe first and show what it found — a video's poster frame, a
        // photo's real proportions — before the long part starts.
        if (!job.probed[i]) {
          job.probed[i] = await probeWithin(f.file, kind)
          const poster = job.probed[i]?.poster
          if (poster && !job.local[i].posterUrl) job.local[i].posterUrl = URL.createObjectURL(poster)
          if (stillPending(messageId)) {
            useMessengerStore.getState().replaceWireMessage({
              id: messageId,
              conversationId: job.message.conversationId,
              attachments: draftAttachments(job),
            } as WireMessage)
          }
        }
        if (controller.signal.aborted) return
        const { attachment } = await uploadAttachmentFile(f.file, kind, {
          signal: controller.signal,
          probed: job.probed[i] ?? undefined,
          durationMs: f.durationMs,
          onProgress: (sent) => {
            if (!stillPending(messageId)) {
              controller.abort()
              return
            }
            job.sent[i] = sent
            report()
          },
        })
        job.uploaded[i] = attachment
        job.sent[i] = f.file.size
        report()
      }),
    )
    if (controller.signal.aborted) return
    // Cleared or deleted while it uploaded: nothing left to send it into.
    if (!stillPending(messageId)) {
      cancelFilesMessage(messageId)
      return
    }

    stage = 'send'
    outbox().patch(messageId, { state: 'sending', progress: 1 })
    const attachments = job.uploaded.filter((a): a is Attachment => a !== null)
    const me = useAuthStore.getState().user
    if (!me?.id) throw new Error('not authenticated')
    const { message: acked } = await sendMessageToConversation(
      me.id,
      job.message.conversationId,
      {
        body: job.message.body,
        clientMsgId: messageId,
        messageType: 'text',
        replyTo: job.message.replyTo ?? null,
        attachments,
      },
      me.name,
    )

    // A photo, or a video's poster, goes on showing from this device rather
    // than downloading itself again the moment the stored URL replaces the
    // local one — see local-media-copies. The rest is released.
    job.message.files.forEach((f, i) => {
      const stored = job.uploaded[i]
      const local = job.local[i]
      if (stored && kindOf(f) === 'photo') rememberLocalCopy(stored.url, local.url)
      else URL.revokeObjectURL(local.url)
      if (local.posterUrl) {
        if (stored?.thumbnailUrl) rememberLocalCopy(stored.thumbnailUrl, local.posterUrl)
        else URL.revokeObjectURL(local.posterUrl)
      }
    })

    const temp = useMessengerStore.getState().pendingSends.get(messageId)
    if (temp) {
      useMessengerStore.getState().reconcilePendingSend(messageId, {
        ...temp,
        id: acked.id ?? messageId,
        body: acked.body ?? temp.body,
        createdAt: (acked as { createdAt?: string }).createdAt ?? temp.createdAt,
        // The stored copies, never the local URLs: this row is what gets
        // cached, and a blob: URL means nothing after a reload.
        attachments,
      })
    }
    jobs.delete(messageId)
    outbox().drop(messageId)
  } catch (err) {
    // The row went while it uploaded (the chat was cleared): tidy up after it.
    if (!stillPending(messageId)) {
      cancelFilesMessage(messageId)
      return
    }
    if (isAbortError(err) || controller.signal.aborted) return
    outbox().patch(messageId, { state: 'failed', error: failureMessage(err, stage) })
  } finally {
    if (job.controller === controller) job.controller = null
  }
}
